/* src/ui/dispatch.ts — the single root event delegator (PLAN.md Part 4 §2/§5, TASKS.md 0.7).
 *
 * No component attaches its own click listener. `el()`'s `data` prop writes `data-action`
 * onto interactive nodes, and one listener per event type on the app root resolves and
 * dispatches. Once a player has cart, inventory, community and leaderboard windows open at
 * once, that is the difference between four listeners and several hundred.
 *
 * Actions that stay inside ui/ are handled by their registering module. Actions that leave
 * ui/ call net/apiClient.ts directly, or emit onto core/events.ts — never by importing
 * render/ or world/, which would break the module boundary from the UI side.
 */

import { emit } from '../core/events.ts';

export type ActionHandler = (e: Event, target: HTMLElement) => void;

const registry = new Map<string, ActionHandler>();

export function registerAction(name: string, handler: ActionHandler): () => void {
  if (registry.has(name)) {
    console.warn(`[dispatch] action "${name}" re-registered; the later handler wins`);
  }
  registry.set(name, handler);
  return () => {
    if (registry.get(name) === handler) registry.delete(name);
  };
}

export function hasAction(name: string): boolean {
  return registry.has(name);
}

/*
 * Right-click is deliberately overloaded: a still right-click opens the context menu, a
 * right-drag pans the camera (PLAN.md Part 4 §5). One shared threshold resolves it, with no
 * dead zone between the two — anything past 6px or 200ms is a pan, anything under is a menu.
 */
const DRAG_THRESHOLD_PX = 6;
const DRAG_THRESHOLD_MS = 200;

interface PointerOrigin {
  x: number;
  y: number;
  t: number;
  moved: boolean;
}

let rightPointer: PointerOrigin | null = null;

function movedPastThreshold(origin: PointerOrigin, x: number, y: number): boolean {
  return Math.hypot(x - origin.x, y - origin.y) > DRAG_THRESHOLD_PX;
}

function handleClick(e: Event): void {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const actionEl = target.closest<HTMLElement>('[data-action]');
  if (!actionEl) return;

  const name = actionEl.dataset['action'];
  if (name === undefined) return;

  const handler = registry.get(name);
  if (!handler) {
    console.warn(`[dispatch] no handler registered for action "${name}"`);
    return;
  }
  handler(e, actionEl);
}

function handlePointerDown(e: PointerEvent): void {
  if (e.button === 2) {
    rightPointer = { x: e.clientX, y: e.clientY, t: performance.now(), moved: false };
    return;
  }
  if (e.button !== 0) return;

  const target = e.target;
  if (!(target instanceof Element)) return;

  // Drag surfaces opt in explicitly: data-drag="window" on a titlebar, "resize" on a handle.
  const dragEl = target.closest<HTMLElement>('[data-drag]');
  if (!dragEl) return;
  const kind = dragEl.dataset['drag'];
  if (kind === undefined) return;

  emit('ui:dragstart', {
    x: e.clientX,
    y: e.clientY,
    pointerId: e.pointerId,
    target: dragEl,
    kind,
  });
}

function handlePointerMove(e: PointerEvent): void {
  if (rightPointer && !rightPointer.moved && movedPastThreshold(rightPointer, e.clientX, e.clientY)) {
    rightPointer.moved = true;
  }
}

function handlePointerUp(): void {
  // Left in place until the following contextmenu reads it; cleared on the next right-down.
}

function handleContextMenu(e: MouseEvent): void {
  const origin = rightPointer;
  const heldTooLong = origin !== null && performance.now() - origin.t > DRAG_THRESHOLD_MS;
  const dragged = origin !== null && (origin.moved || movedPastThreshold(origin, e.clientX, e.clientY));
  rightPointer = null;

  // A pan gesture: swallow the native menu and let the camera keep the drag.
  if (dragged || heldTooLong) {
    e.preventDefault();
    return;
  }

  const target = e.target;
  if (!(target instanceof HTMLElement)) return;

  e.preventDefault();
  emit('ui:contextmenu', { x: e.clientX, y: e.clientY, target });
}

function handleKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  emit('ui:escape');
}

/** Install the delegators. Called once, from main.ts, on the `#gf-app` root. */
export function installRootDispatcher(root: HTMLElement): () => void {
  root.addEventListener('click', handleClick);
  root.addEventListener('pointerdown', handlePointerDown);
  root.addEventListener('contextmenu', handleContextMenu);
  // Move/up and Escape are window-scoped on purpose: a drag that leaves the root element,
  // or a keypress while focus sits on <body>, must still resolve.
  window.addEventListener('pointermove', handlePointerMove);
  window.addEventListener('pointerup', handlePointerUp);
  window.addEventListener('keydown', handleKeyDown);

  return () => {
    root.removeEventListener('click', handleClick);
    root.removeEventListener('pointerdown', handlePointerDown);
    root.removeEventListener('contextmenu', handleContextMenu);
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    window.removeEventListener('keydown', handleKeyDown);
  };
}
